import { FileInput, FileUploader } from '@/components/file-uploader/file-uploader';
import { useTranslation } from 'react-i18next';
import { FormControl, FormField, FormItem, FormLabel } from '@/components/ui-kit/form';
import { Separator } from '@/components/ui-kit/separator';
import { useFormContext } from 'react-hook-form';
import { dropzoneOptions } from './utils';
import { CloudUpload, Trash } from 'lucide-react';
import { Textarea } from '@/components/ui-kit/textarea';
import { Button } from '@/components/ui-kit/button';
import { useProjectStore } from '@/modules/identifier/state/use-project-store';
import { BaseWidget } from '@/modules/gpt-chats/components/agent-integrations/widgets/index.type';
import { Accordion, AccordionItem, AccordionTrigger } from '@/components/ui-kit/accordion';
import { AccordionContent } from '@radix-ui/react-accordion';
import { useGetPreSignedUrlForUpload, useUploadFile } from '@/lib/api/hooks/use-storage-file';
import { getFileByFileId } from '@/lib/api/services/storage.service';
import { Switch } from '@/components/ui-kit/switch';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import { ModuleName } from '@/constant/modules.constants';
import { showErrorToast } from '@/hooks/use-toast';

export const AgentIntegrationGlobalSettings = () => {
  const { t } = useTranslation();
  const form = useFormContext<BaseWidget>();
  const { tenantId } = useProjectStore().selectedProject || { tenantId: '' };
  const { mutateAsync: preSignedUrlForUploadMutate } = useGetPreSignedUrlForUpload();
  const { mutateAsync: uploadfileMutate } = useUploadFile();
  const [isUploading, setIsUploading] = useState<boolean>(false);

  const fileUploadHandler = async (file: File, setFile: (value: string) => void) => {
    try {
      setIsUploading(true);
      const payload = {
        name: file.name,
        projectKey: tenantId,
        configurationName: 'Default',
        accessModifier: 'Public',
        metaData: '',
        parentDirectoryId: '',
        tags: '',
        moduleName: ModuleName.AICloud,
      };
      const preSignedUrlRes = await preSignedUrlForUploadMutate(payload);
      if (!preSignedUrlRes.isSuccess) throw new Error('Failed to get upload URL');
      await uploadfileMutate({ url: preSignedUrlRes.uploadUrl, file });
      const fileInfo = await getFileByFileId({
        itemId: preSignedUrlRes.fileId || '',
        projectKey: tenantId,
      });
      setFile(fileInfo.url);
      form.setValue('designSettings.banner.imageId', fileInfo.itemId);
    } catch (_error) {
      //
    } finally {
      setIsUploading(false);
    }
  };
  const { isValid } = form.formState;
  return (
    <div className="grid gap-4">
      <div className="text-sm font-normal text-medium-emphasis">
        <span className="font-semibold">{t('NOTE_LABEL_STAR')}:</span> {t('GLOBAL_SETTING_NOTE')}
      </div>
      <Separator />
      {/* <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Name</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="logo_url"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Logo</FormLabel>
            <FormControl>
              {field.value ? (
                <div className="flex items-start justify-between bg-surface-app p-3">
                  <div className="h-10 max-h-10 max-w-10 overflow-hidden rounded-sm">
                    <Image src={field.value} width="40" height="40" alt="widget-logo" />
                  </div>
                  <Button
                    variant="ghost"
                    className="h-fit w-fit p-0"
                    onClick={() => {
                      field.onChange("");
                      form.setValue("logo_id", "");
                    }}
                  >
                    <Trash className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <FileUploader
                  value={[]}
                  onValueChange={(files) => {
                    if (files && files[0]) {
                      fileUploadHandler(files[0], field.onChange);
                    }
                  }}
                  dropzoneOptions={dropzoneOptions}
                >
                  <FileInput className="flex w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-4">
                    <CloudUpload className="h-8 w-8 text-medium-emphasis" />
                    <p className="text-sm leading-5">
                      <span className="font-semibold text-primary">Click to upload</span>
                      <span className="font-normal"> or drag and drop</span>
                    </p>
                  </FileInput>
                </FileUploader>
              )}
            </FormControl>
          </FormItem>
        )}
      /> */}
      <Accordion type="single" collapsible>
        <AccordionItem value="header" className="border-b-0">
          <AccordionTrigger className="hover:no-underline">{t('BANNER')}</AccordionTrigger>
          <AccordionContent className="grid gap-4">
            <FormField
              control={form.control}
              name="designSettings.banner.enabled"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between">
                  <FormLabel>{t('SHOW_BANNER')}</FormLabel>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="designSettings.banner.imageUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('IMAGE_LABEL')}</FormLabel>
                  <FormControl>
                    {field.value ? (
                      <div className="flex items-start justify-between rounded-sm bg-muted p-3">
                        <div className="relative h-20 max-h-20 flex-1 overflow-hidden rounded-sm">
                          <img
                            src={field.value}
                            alt="widget-banner"
                            className="h-full w-full object-contain"
                          />
                        </div>
                        <Button
                          variant="ghost"
                          className="h-fit w-fit p-0"
                          onClick={() => {
                            field.onChange('');
                            form.setValue('designSettings.banner.imageId', '');
                          }}
                        >
                          <Trash className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <FileUploader
                        value={[]}
                        onValueChange={(files) => {
                          if (!files || !files[0]) return;
                          const file = files[0];

                          if (file.size >= 1 * 1024 * 1024) {
                            showErrorToast({ errors: t('FILE_SIZE_LIMIT_ERROR', { name: t('IMAGE_LABEL'), size: 1 }) });
                            return;
                          }
                          fileUploadHandler(file, field.onChange);
                        }}
                        dropzoneOptions={dropzoneOptions}
                      >
                        <FileInput
                          className={cn(
                            'relative flex h-[100px] w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-4',
                            isUploading && 'pointer-events-none opacity-50'
                          )}
                        >
                          {!isUploading && (
                            <>
                              <CloudUpload className="h-8 w-8 text-medium-emphasis" />
                              <p className="text-sm leading-5">
                                <span className="font-semibold text-primary">{t('CLICK_TO_UPLOAD')}</span>
                                <span className="font-normal">{t('OR_DRAG_AND_DROP')}</span>
                                <span className="font-normal"> {t('MAX_1MB_HINT')}</span>
                              </p>
                            </>
                          )}
                          {isUploading && (
                            <div className="absolute text-sm text-medium-emphasis">{t('UPLOADING')}</div>
                          )}
                        </FileInput>
                      </FileUploader>
                    )}
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="designSettings.banner.text"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('TEXT')}</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={5} />
                  </FormControl>
                </FormItem>
              )}
            />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      <FormField
        control={form.control}
        name="greeting"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('GREETING_TEXT')}</FormLabel>
            <FormControl>
              <Textarea {...field} rows={5} />
            </FormControl>
          </FormItem>
        )}
      />
      <Button disabled={!isValid || isUploading}>{t('SAVE')}</Button>
    </div>
  );
};
